import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Calendar, ChevronLeft, ChevronRight, Plus, X, Clock, User, Phone, Mail, MapPin, CheckCircle, XCircle, Edit2, Trash2 } from 'lucide-react'
import { db } from '../../firebase'
import { collection, addDoc, updateDoc, deleteDoc, doc, onSnapshot, serverTimestamp, query, where, orderBy } from 'firebase/firestore'
import { formatDate } from '../../utils/helpers.jsx'

const CalendarView = () => {
  const [currentDate, setCurrentDate] = useState(new Date())
  const [selectedDate, setSelectedDate] = useState(null)
  const [consultations, setConsultations] = useState([])
  const [filteredConsultations, setFilteredConsultations] = useState([])
  const [showAddModal, setShowAddModal] = useState(false)
  const [editingConsultation, setEditingConsultation] = useState(null)
  const [viewMode, setViewMode] = useState('month') // month, week, day
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    date: '',
    time: '',
    duration: '60',
    notes: '',
    status: 'scheduled'
  })

  useEffect(() => {
    const consultationsRef = collection(db, 'consultations')
    const unsubscribe = onSnapshot(consultationsRef, (snapshot) => {
      const loaded = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        consultationDate: doc.data().date ? new Date(doc.data().date) : null
      }))
      setConsultations(loaded)
    })
    return () => unsubscribe()
  }, [])

  useEffect(() => {
    if (selectedDate) {
      const filtered = consultations.filter(c => {
        if (!c.consultationDate) return false
        return c.consultationDate.toDateString() === selectedDate.toDateString()
      })
      setFilteredConsultations(filtered)
    } else {
      setFilteredConsultations(consultations)
    }
  }, [selectedDate, consultations])

  const getDaysInMonth = (date) => {
    const year = date.getFullYear()
    const month = date.getMonth()
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const daysInMonth = lastDay.getDate()
    const startingDayOfWeek = firstDay.getDay()

    const days = []
    // Add empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null)
    }
    // Add days of month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i))
    }
    return days
  }

  const getConsultationsForDate = (date) => {
    if (!date) return []
    return consultations.filter(c => {
      if (!c.consultationDate) return false
      return c.consultationDate.toDateString() === date.toDateString()
    })
  }

  const handlePreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1))
  }

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1))
  }

  const handleDateClick = (date) => {
    setSelectedDate(date)
  }

  const handleAddConsultation = async (e) => {
    e.preventDefault()
    try {
      const consultationDateTime = `${formData.date}T${formData.time}`
      
      if (editingConsultation) {
        await updateDoc(doc(db, 'consultations', editingConsultation.id), {
          ...formData,
          consultationDateTime,
          updatedAt: serverTimestamp()
        })
      } else {
        await addDoc(collection(db, 'consultations'), {
          ...formData,
          consultationDateTime,
          createdAt: serverTimestamp()
        })
      }

      setShowAddModal(false)
      setEditingConsultation(null)
      setFormData({
        name: '',
        email: '',
        phone: '',
        service: '',
        date: '',
        time: '',
        duration: '60',
        notes: '',
        status: 'scheduled'
      })
    } catch (error) {
      console.error('Error saving consultation:', error)
      alert('❌ Gabim gjatë ruajtjes!')
    }
  }

  const handleEdit = (consultation) => {
    setEditingConsultation(consultation)
    setFormData({
      name: consultation.name || '',
      email: consultation.email || '',
      phone: consultation.phone || '',
      service: consultation.service || '',
      date: consultation.date || '',
      time: consultation.time || '',
      duration: consultation.duration || '60',
      notes: consultation.notes || '',
      status: consultation.status || 'scheduled'
    })
    setShowAddModal(true)
  }

  const handleDelete = async (id) => {
    if (window.confirm('Dëshiron ta fshish këtë konsultim?')) {
      try {
        await deleteDoc(doc(db, 'consultations', id))
      } catch (error) {
        console.error('Error deleting:', error)
      }
    }
  }

  const handleStatusChange = async (id, newStatus) => {
    try {
      await updateDoc(doc(db, 'consultations', id), {
        status: newStatus,
        updatedAt: serverTimestamp()
      })
    } catch (error) {
      console.error('Error updating status:', error)
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled': return 'bg-blue-500/20 text-blue-400 border-blue-500/30'
      case 'confirmed': return 'bg-green-500/20 text-green-400 border-green-500/30'
      case 'completed': return 'bg-purple-500/20 text-purple-400 border-purple-500/30'
      case 'cancelled': return 'bg-red-500/20 text-red-400 border-red-500/30'
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30'
    }
  }

  const getStatusLabel = (status) => {
    switch (status) {
      case 'scheduled': return '📅 E planifikuar'
      case 'confirmed': return '✅ E konfirmuar'
      case 'completed': return '✔️ E përfunduar'
      case 'cancelled': return '❌ E anuluar'
      default: return status
    }
  }

  const monthNames = ['Janar', 'Shkurt', 'Mars', 'Prill', 'Maj', 'Qershor', 'Korrik', 'Gusht', 'Shtator', 'Tetor', 'Nëntor', 'Dhjetor']
  const dayNames = ['Die', 'Hën', 'Mar', 'Mër', 'Enj', 'Pre', 'Sht']

  const days = getDaysInMonth(currentDate)
  const today = new Date()

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-display font-bold text-gold-400 flex items-center">
            <Calendar className="w-6 h-6 mr-2" />
            Kalendar Konsultimesh
          </h2>
          <p className="text-beige-300 text-sm mt-1">
            {consultations.length} konsultime totale
          </p>
        </div>
        <button
          onClick={() => {
            setShowAddModal(true)
            setEditingConsultation(null)
          }}
          className="btn-primary"
        >
          <Plus className="w-4 h-4 mr-2" />
          Shto Konsultim
        </button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Calendar */}
        <div className="lg:col-span-2 bg-bronze-900/30 backdrop-blur-sm border border-gold-500/20 rounded-2xl p-6">
          {/* Calendar Header */}
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={handlePreviousMonth}
              className="p-2 hover:bg-gold-500/20 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-gold-400" />
            </button>
            <h3 className="text-xl font-display font-bold text-cream">
              {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
            </h3>
            <button
              onClick={handleNextMonth}
              className="p-2 hover:bg-gold-500/20 rounded-lg transition-colors"
            >
              <ChevronRight className="w-5 h-5 text-gold-400" />
            </button>
          </div>

          {/* Day Names */}
          <div className="grid grid-cols-7 gap-2 mb-2">
            {dayNames.map(day => (
              <div key={day} className="text-center text-xs font-semibold text-beige-400 py-2">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Days */}
          <div className="grid grid-cols-7 gap-2">
            {days.map((date, index) => {
              if (!date) {
                return <div key={index} className="aspect-square" />
              }

              const dayConsultations = getConsultationsForDate(date)
              const isToday = date.toDateString() === today.toDateString()
              const isSelected = selectedDate && date.toDateString() === selectedDate.toDateString()
              const hasConsultations = dayConsultations.length > 0

              return (
                <motion.button
                  key={index}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handleDateClick(date)}
                  className={`aspect-square rounded-lg p-2 text-sm font-medium transition-all relative ${
                    isSelected
                      ? 'bg-gradient-gold text-white shadow-gold'
                      : isToday
                      ? 'bg-gold-500/30 text-gold-300 border border-gold-500/50'
                      : hasConsultations
                      ? 'bg-blue-500/20 text-blue-300 hover:bg-blue-500/30'
                      : 'bg-dark/30 text-beige-300 hover:bg-dark/50'
                  }`}
                >
                  <span className="block">{date.getDate()}</span>
                  {hasConsultations && (
                    <div className="absolute bottom-1 left-1/2 -translate-x-1/2 flex space-x-1">
                      {dayConsultations.slice(0, 3).map((_, i) => (
                        <div key={i} className="w-1 h-1 bg-blue-400 rounded-full" />
                      ))}
                    </div>
                  )}
                </motion.button>
              )
            })}
          </div>

          {/* Today Button */}
          <div className="mt-4 text-center">
            <button
              onClick={() => {
                const now = new Date()
                setCurrentDate(now)
                setSelectedDate(now)
              }}
              className="btn-secondary text-sm"
            >
              Sot
            </button>
          </div>
        </div>

        {/* Consultations List */}
        <div className="bg-bronze-900/30 backdrop-blur-sm border border-gold-500/20 rounded-2xl p-6">
          <h3 className="text-lg font-display font-bold text-gold-400 mb-4">
            {selectedDate 
              ? `Konsultime - ${formatDate(selectedDate, 'long')}`
              : 'Të Gjitha Konsultimet'
            }
          </h3>

          <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2">
            <AnimatePresence>
              {filteredConsultations.length > 0 ? (
                filteredConsultations.map((consultation) => (
                  <motion.div
                    key={consultation.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="bg-dark/50 rounded-xl p-4 border border-gold-500/10 hover:border-gold-500/30 transition-all"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h4 className="font-semibold text-cream flex items-center">
                          <User className="w-4 h-4 mr-1" />
                          {consultation.name}
                        </h4>
                        <p className="text-xs text-beige-400 mt-1">
                          <Clock className="w-3 h-3 inline mr-1" />
                          {consultation.time} ({consultation.duration || '60'} min)
                        </p>
                      </div>
                      <div className="flex space-x-1">
                        <button
                          onClick={() => handleEdit(consultation)}
                          className="p-1.5 hover:bg-blue-500/20 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Edit2 className="w-3.5 h-3.5 text-blue-400" />
                        </button>
                        <button
                          onClick={() => handleDelete(consultation.id)}
                          className="p-1.5 hover:bg-red-500/20 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-3.5 h-3.5 text-red-400" />
                        </button>
                      </div>
                    </div>

                    <div className="space-y-1 text-xs text-beige-300 mb-3">
                      {consultation.service && (
                        <p>📋 {consultation.service}</p>
                      )}
                      {consultation.email && (
                        <p className="flex items-center">
                          <Mail className="w-3 h-3 mr-1" />
                          {consultation.email}
                        </p>
                      )}
                      {consultation.phone && (
                        <p className="flex items-center">
                          <Phone className="w-3 h-3 mr-1" />
                          {consultation.phone}
                        </p>
                      )}
                    </div>

                    {consultation.notes && (
                      <p className="text-xs text-beige-400 mb-3 p-2 bg-dark/30 rounded">
                        {consultation.notes}
                      </p>
                    )}

                    <div className="flex items-center justify-between">
                      <select
                        value={consultation.status}
                        onChange={(e) => handleStatusChange(consultation.id, e.target.value)}
                        className={`text-xs px-2 py-1 rounded-lg border ${getStatusColor(consultation.status)} cursor-pointer`}
                      >
                        <option value="scheduled">📅 E planifikuar</option>
                        <option value="confirmed">✅ E konfirmuar</option>
                        <option value="completed">✔️ E përfunduar</option>
                        <option value="cancelled">❌ E anuluar</option>
                      </select>
                    </div>
                  </motion.div>
                ))
              ) : (
                <div className="text-center py-12">
                  <Calendar className="w-12 h-12 text-beige-400 mx-auto mb-3 opacity-50" />
                  <p className="text-beige-300 text-sm">
                    {selectedDate 
                      ? 'Nuk ka konsultime për këtë datë'
                      : 'Nuk ka konsultime ende'
                    }
                  </p>
                </div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {showAddModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowAddModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-bronze-900 border border-gold-500/30 rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-display font-bold text-gold-400">
                  {editingConsultation ? 'Edito Konsultimin' : 'Shto Konsultim të Ri'}
                </h3>
                <button
                  onClick={() => {
                    setShowAddModal(false)
                    setEditingConsultation(null)
                  }}
                  className="p-2 hover:bg-red-500/20 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-red-400" />
                </button>
              </div>

              <form onSubmit={handleAddConsultation} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-beige-200 text-sm mb-2">Emri *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="w-full px-4 py-3 bg-dark/50 border border-gold-500/30 rounded-xl text-cream placeholder-beige-400 focus:border-gold-400 focus:ring-2 focus:ring-gold-500/20 transition-all outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-beige-200 text-sm mb-2">Email *</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      className="w-full px-4 py-3 bg-dark/50 border border-gold-500/30 rounded-xl text-cream placeholder-beige-400 focus:border-gold-400 focus:ring-2 focus:ring-gold-500/20 transition-all outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-beige-200 text-sm mb-2">Telefon *</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      required
                      className="w-full px-4 py-3 bg-dark/50 border border-gold-500/30 rounded-xl text-cream placeholder-beige-400 focus:border-gold-400 focus:ring-2 focus:ring-gold-500/20 transition-all outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-beige-200 text-sm mb-2">Shërbimi *</label>
                    <select
                      value={formData.service}
                      onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                      required
                      className="w-full px-4 py-3 bg-dark/50 border border-gold-500/30 rounded-xl text-cream focus:border-gold-400 focus:ring-2 focus:ring-gold-500/20 transition-all outline-none"
                    >
                      <option value="">Zgjidh shërbimin</option>
                      <option value="Website Development">Website Development</option>
                      <option value="Social Media Management">Social Media Management</option>
                      <option value="Branding & Design">Branding & Design</option>
                      <option value="SEO & Google Ads">SEO & Google Ads</option>
                      <option value="Consultation">Konsultim i Përgjithshëm</option>
                      <option value="Other">Tjetër</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-beige-200 text-sm mb-2">Data *</label>
                    <input
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      required
                      className="w-full px-4 py-3 bg-dark/50 border border-gold-500/30 rounded-xl text-cream focus:border-gold-400 focus:ring-2 focus:ring-gold-500/20 transition-all outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-beige-200 text-sm mb-2">Ora *</label>
                    <input
                      type="time"
                      value={formData.time}
                      onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                      required
                      className="w-full px-4 py-3 bg-dark/50 border border-gold-500/30 rounded-xl text-cream focus:border-gold-400 focus:ring-2 focus:ring-gold-500/20 transition-all outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-beige-200 text-sm mb-2">Kohëzgjatja (min)</label>
                    <select
                      value={formData.duration}
                      onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                      className="w-full px-4 py-3 bg-dark/50 border border-gold-500/30 rounded-xl text-cream focus:border-gold-400 focus:ring-2 focus:ring-gold-500/20 transition-all outline-none"
                    >
                      <option value="30">30 min</option>
                      <option value="60">60 min</option>
                      <option value="90">90 min</option>
                      <option value="120">120 min</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-beige-200 text-sm mb-2">Statusi</label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      className="w-full px-4 py-3 bg-dark/50 border border-gold-500/30 rounded-xl text-cream focus:border-gold-400 focus:ring-2 focus:ring-gold-500/20 transition-all outline-none"
                    >
                      <option value="scheduled">📅 E planifikuar</option>
                      <option value="confirmed">✅ E konfirmuar</option>
                      <option value="completed">✔️ E përfunduar</option>
                      <option value="cancelled">❌ E anuluar</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-beige-200 text-sm mb-2">Shënime (opsionale)</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-3 bg-dark/50 border border-gold-500/30 rounded-xl text-cream placeholder-beige-400 focus:border-gold-400 focus:ring-2 focus:ring-gold-500/20 transition-all outline-none resize-none"
                    placeholder="Shënime shtesë..."
                  />
                </div>

                <div className="flex space-x-3">
                  <button type="submit" className="btn-primary flex-1">
                    {editingConsultation ? 'Ruaj Ndryshimet' : 'Shto Konsultimin'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddModal(false)
                      setEditingConsultation(null)
                    }}
                    className="btn-secondary flex-1"
                  >
                    Anulo
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default CalendarView
